import { useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { DocumentTextIcon, InformationCircleIcon, RocketLaunchIcon } from '@heroicons/react/24/outline';

import { Button, Link, TextArea, Heading } from '@restworld/ui-ds-base';

import TipsFromOurTeamModal from './tips-from-our-team-modal';
import { CoverLetterQuickGuide } from './cover-letter-quick-guide';

const MAX_COVER_LETTER_LENGTH = 2000;

export const CoverLetterInputArea = ({
  onShowCoverLetterGuide,
  onApplyWithCoverLetter,
  loading,
  coverLetter,
  setCoverLetterInput,
  isSpontaneousApplication,
  showCoverLetterGuide = true,
  isError,
}: {
  onShowCoverLetterGuide: () => void;
  onApplyWithCoverLetter?: () => void;
  loading: boolean;
  coverLetter: string;
  setCoverLetterInput: (x: string) => void;
  showCoverLetterGuide?: boolean;
  isError?: boolean;
  isSpontaneousApplication?: boolean;
}) => {
  const { t } = useTranslation();
  const [isTipsModalOpen, setIsTipsModalOpen] = useState(false);

  const submitButton = useMemo(
    () => (
      <Button
        EndIcon={isSpontaneousApplication ? undefined : RocketLaunchIcon}
        onClick={onApplyWithCoverLetter}
        loading={loading}
        disabled={!coverLetter}
        fullwidth
      >
        {t(
          isSpontaneousApplication ? 'onboarding-page:submit_spontaneous_application' : 'common:send_your_application'
        )}
      </Button>
    ),
    [isSpontaneousApplication, onApplyWithCoverLetter, loading, coverLetter, t]
  );

  return (
    <div className="flex flex-row gap-4 w-full justify-center">
      {showCoverLetterGuide ? (
        <div className="border-r border-gray-300 pr-8 max-w-xs">
          <CoverLetterQuickGuide showCloseButton={false} />
        </div>
      ) : null}
      <div className="flex flex-col gap-32">
        <div className="flex flex-col gap-4 items-center text-center">
          <div className="flex flex-col gap-2 items-center">
            <Heading size={showCoverLetterGuide ? 2 : 4} weight="semibold" className="leading-6.75 max-w-sm">
              {t('onboarding-page:write_the_cover_letter')}
            </Heading>
            <Heading size={5} weight="normal" color="muted" className="leading-6">
              {t('onboarding-page:write_a_short_presentation')}
            </Heading>
          </div>
          <div className="max-w-lg max-h-fit min-h-65.25 h-full w-full p-4 rounded-lg flex gap-10 flex-col">
            <div className="flex gap-2 flex-col">
              <h6 className="flex justify-between items-center">
                <span className="text-neutral-700 !text-base font-medium">
                  {t('onboarding-page:why_are_you_applying')}
                </span>
                <span className="!text-sm text-neutral-100">{`${
                  coverLetter?.length || 0
                } / ${MAX_COVER_LETTER_LENGTH}`}</span>
              </h6>
              <TextArea
                id="cover letter"
                placeholder={t('onboarding-page:write_your_presentation')}
                rows={showCoverLetterGuide ? 13 : 8}
                maxLength={MAX_COVER_LETTER_LENGTH}
                onChange={(value) => setCoverLetterInput(value)}
                value={coverLetter}
                className={'min-w-full w-full border-neutral-90 focus:!border-neutral-100 focus:!ring-neutral-100'}
                isError={isError}
                showMaxLength={false}
              />
              <div className="flex items-center">
                <Link StartIcon={InformationCircleIcon} onClick={() => setIsTipsModalOpen(true)} newTab={false}>
                  {t('onboarding-page:four_tips_from_our_team')}
                </Link>
              </div>
              {isError && <div className="text-red-500 text-sm">{t('common:something_went_wrong')}</div>}
            </div>
            {showCoverLetterGuide ? submitButton : null}
          </div>
        </div>
        {!showCoverLetterGuide ? (
          <div className="flex flex-col gap-2">
            {!isSpontaneousApplication && (
              <Button variant="tertiary" StartIcon={DocumentTextIcon} fullwidth onClick={onShowCoverLetterGuide}>
                {t('onboarding-page:open_the_guide')}
              </Button>
            )}
            {submitButton}
          </div>
        ) : null}
      </div>
      <TipsFromOurTeamModal isOpen={isTipsModalOpen} onClose={() => setIsTipsModalOpen(false)} />
    </div>
  );
};
